
import React from 'react';
import { Card } from "@/components/ui/card";
import { Lightbulb, BookOpen, Brain, Trophy } from 'lucide-react';
import type { PerformanceData } from './types';

interface RecommendationSystemProps {
  performanceData: PerformanceData[];
}

interface SubjectPerformance {
  subject: string;
  averageScore: number;
  attempts: number;
  trend: 'improving' | 'declining' | 'stable';
}

export const RecommendationSystem: React.FC<RecommendationSystemProps> = ({ performanceData }) => {
  const subjectPerformance = React.useMemo((): SubjectPerformance[] => {
    if (!performanceData.length) return [];

    const subjectData = performanceData.reduce((acc, curr) => {
      if (!acc[curr.subject]) {
        acc[curr.subject] = {
          scores: [],
          timestamps: []
        };
      }
      acc[curr.subject].scores.push(curr.score);
      acc[curr.subject].timestamps.push(new Date(curr.created_at));
      return acc;
    }, {} as Record<string, { scores: number[], timestamps: Date[] }>);

    return Object.entries(subjectData).map(([subject, data]) => {
      const averageScore = data.scores.reduce((a, b) => a + b, 0) / data.scores.length;
      
      // Calculate trend based on last 3 attempts
      const recentScores = data.scores.slice(-3);
      const trend = recentScores.length >= 2 
        ? recentScores[recentScores.length - 1] > recentScores[0]
          ? 'improving'
          : recentScores[recentScores.length - 1] < recentScores[0]
            ? 'declining'
            : 'stable'
        : 'stable';

      return {
        subject,
        averageScore,
        attempts: data.scores.length,
        trend
      };
    });
  }, [performanceData]);

  const getRecommendations = (performance: SubjectPerformance) => {
    const { averageScore, attempts, trend } = performance;

    if (averageScore >= 80) {
      return {
        icon: <Trophy className="w-5 h-5 text-purple-500" />,
        title: "Expert Level Achievement",
        recommendations: [
          "Move to advanced topics",
          "Try competitive exam questions",
          "Consider tutoring others"
        ]
      };
    }

    if (averageScore >= 60 && averageScore < 80) {
      return {
        icon: <Brain className="w-5 h-5 text-blue-500" />,
        title: "Intermediate Progress",
        recommendations: [
          "Focus on challenging concepts",
          "Increase practice frequency",
          "Review past mistakes"
        ]
      };
    }

    if (averageScore < 60 && trend === 'improving') {
      return {
        icon: <BookOpen className="w-5 h-5 text-green-500" />,
        title: "Building Foundations",
        recommendations: [
          "Continue with foundational topics",
          "Take more practice quizzes",
          "Use study materials regularly"
        ]
      };
    }

    return {
      icon: <Lightbulb className="w-5 h-5 text-yellow-500" />,
      title: "Getting Started",
      recommendations: [
        "Focus on basic concepts",
        "Use learning resources",
        "Take beginner-level quizzes"
      ]
    };
  };

  if (!subjectPerformance.length) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-purple-500" />
          Learning Recommendations
        </h2>
        <p className="text-gray-500">Complete more quizzes to receive personalized recommendations.</p>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
        <Lightbulb className="w-5 h-5 text-purple-500" />
        Learning Recommendations
      </h2>
      <div className="space-y-6">
        {subjectPerformance.map((performance) => {
          const recommendation = getRecommendations(performance);
          return (
            <div key={performance.subject} className="border rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {recommendation.icon}
                  <h3 className="font-semibold">{performance.subject}</h3>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">
                    Average: {performance.averageScore.toFixed(1)}%
                  </span>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    performance.trend === 'improving' ? 'bg-green-100 text-green-800' :
                    performance.trend === 'declining' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {performance.trend === 'improving' ? '↑ Improving' :
                     performance.trend === 'declining' ? '↓ Declining' :
                     '→ Stable'}
                  </span>
                </div>
              </div>
              <div>
                <h4 className="font-medium text-sm mb-2">{recommendation.title}</h4>
                <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                  {recommendation.recommendations.map((rec, index) => (
                    <li key={index}>{rec}</li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};
